import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

/**
 * Cycle 8 STORY-701: Animated stats counter for HomePage.
 *
 * Counts up to target value over ~1.5 seconds using requestAnimationFrame.
 * Triggers when component enters viewport (IntersectionObserver).
 * Respects prefers-reduced-motion (skips animation, shows final value immediately).
 */

interface StatItem {
  /** Final value to count to */
  value: number;
  /** Optional suffix (e.g. "+", "%", " лет") */
  suffix?: string;
  /** Label below the number */
  label: string;
  /** Sublabel (smaller, optional context) */
  sublabel?: string;
}

const STATS: StatItem[] = [
  { value: 12, label: "Программных продуктов", sublabel: "в едином портфеле" },
  { value: 5, label: "Индустрий покрыто", sublabel: "производство, логистика, retail, energy, HSE" },
  { value: 100, suffix: "%", label: "Production-ready", sublabel: "12 demo URLs HTTP 200 непрерывно" },
  { value: 25, suffix: "+", label: "Лет совместной экспертизы", sublabel: "core team + R&D-партнёры" },
];

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

interface AnimatedNumberProps {
  target: number;
  durationMs?: number;
  trigger: boolean;
  reduced: boolean;
}

function AnimatedNumber({
  target,
  durationMs = 1500,
  trigger,
  reduced,
}: AnimatedNumberProps): ReactElement {
  const [current, setCurrent] = useState(reduced ? target : 0);

  useEffect(() => {
    if (!trigger) return;
    if (reduced) {
      setCurrent(target);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setCurrent(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, trigger, reduced]);

  return <>{current}</>;
}

export function AnimatedStats(): ReactElement {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!sectionRef.current) return;

    // Defensive: check if element is already in viewport on mount
    // (handles cases where IntersectionObserver doesn't fire reliably,
    // e.g., headless browsers, post-navigation remounts)
    const rect = sectionRef.current.getBoundingClientRect();
    const inViewportOnMount =
      rect.top < window.innerHeight && rect.bottom > 0;
    if (inViewportOnMount) {
      setVisible(true);
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="animated-stats"
      aria-label="Ключевые метрики платформы"
    >
      <div className="animated-stats__grid">
        {STATS.map((stat) => (
          <div key={stat.label} className="animated-stats__item">
            <div className="animated-stats__value">
              <AnimatedNumber
                target={stat.value}
                trigger={visible}
                reduced={reduced}
              />
              {stat.suffix && <span className="animated-stats__suffix">{stat.suffix}</span>}
            </div>
            <div className="animated-stats__label">{stat.label}</div>
            {stat.sublabel && (
              <div className="animated-stats__sublabel">{stat.sublabel}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
